import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { SavedAccountsList } from '@/components/watchlists/saved-accounts-list'
import { watchlistService } from '@/services/watchlist-service'
import { userService } from '@/services/user-service'
import { useToast } from '@/hooks/use-toast'
import { Rocket, Loader2 } from 'lucide-react'

interface EmptyStateProps {
  onCreate: () => void
  onWatchlistCreated?: (watchlistId: string) => void
}

export function EmptyState({ onWatchlistCreated }: EmptyStateProps) {
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([])
  const [isCreating, setIsCreating] = useState(false)
  const { toast } = useToast()

  const handleAccountToggle = (accountId: string) => {
    setSelectedAccounts(prev => 
      prev.includes(accountId)
        ? prev.filter(id => id !== accountId)
        : [...prev, accountId]
    )
  }

  const handleCreateCompetitorWatchlist = async () => {
    if (selectedAccounts.length === 0) {
      toast({
        title: "No accounts selected",
        description: "Please select at least one account to create a competitor watchlist.",
        variant: "destructive",
      })
      return
    }
    
    setIsCreating(true)
    
    try {
      // Step 1: Create the watchlist
      const watchlistResponse = await watchlistService.createWatchlist({
        name: "Competitor Watchlist",
        description: "Track your competitors' content and performance",
        account_uuids: selectedAccounts
      })
      
      if (watchlistResponse.success && watchlistResponse.watchlist_id) {
        // Step 2: Update user profile competitor list with the new watchlist ID  
        await userService.setCompetitorList(watchlistResponse.watchlist_id)
        
        toast({
          title: "Competitor Watchlist Created",
          description: `Successfully created watchlist with ${selectedAccounts.length} accounts.`,
          variant: "default",
        })
        
        // Notify parent component
        onWatchlistCreated?.(watchlistResponse.watchlist_id)
      } else {
        throw new Error(watchlistResponse.message || 'Failed to create watchlist')
      }
    } catch (error) {
      console.error('Failed to create competitor watchlist:', error)
      toast({
        title: "Error",
        description: `Failed to create competitor watchlist: ${error instanceof Error ? error.message : 'Unknown error'}`,
        variant: "destructive",
      })
    } finally {
      setIsCreating(false)
    }
  }

  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Rocket className="h-5 w-5" />
            Competitor Watchlist
          </CardTitle>
          <Button 
            onClick={handleCreateCompetitorWatchlist}
            disabled={selectedAccounts.length === 0 || isCreating}
            className="min-w-[180px]"
          >
            {isCreating ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Creating...
              </>
            ) : (
              `Create Competitor Watchlist${selectedAccounts.length > 0 ? ` (${selectedAccounts.length})` : ''}`
            )}
          </Button>
        </div>
        <p className="text-sm text-muted-foreground mt-2">
          Select accounts from your analyzed profiles to create a competitor watchlist
        </p>
      </CardHeader>
      <CardContent>
        <SavedAccountsList 
          selectedAccounts={selectedAccounts}
          onAccountToggle={handleAccountToggle}
        />
      </CardContent>
    </Card>
  )
}


